import { useEffect, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";




const EMPTY_ROW = { category: "", short_form: "", description: "", type: "" };

const TYPE_COLORS = {
  product:  { bg: "#e6f1fb", color: "#185fa5" },
  channel:  { bg: "#eaf3de", color: "#3b6d11" },
  business: { bg: "#faeeda", color: "#854f0b" },
};

export default function CategoryMaster() {
  const [rows, setRows]       = useState([]);
  const [editId, setEditId]   = useState(null);   // id being edited, or "new"
  const [form, setForm]       = useState({});
  const [search, setSearch]   = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const CATEGORY_URL = `${BASE_URL}/categories/`;

  useEffect(() => {
    loadCategories();
  }, []);

  const types = [...new Set(rows.map((r) => r.type).filter(Boolean))];

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchQ =
      !q
      || (r.category || "").toLowerCase().includes(q)
      || (r.short_form || "").toLowerCase().includes(q)
      || (r.description || "").toLowerCase().includes(q);
    const matchT = !typeFilter || r.type === typeFilter;
    return matchQ && matchT;
  });

  function startAdd() {
    setEditId("new");
    setForm(EMPTY_ROW);
  }

  function startEdit(row) {
    setEditId(row.id);
    setForm({
      category: row.category || "",
      short_form: row.short_form || "",
      description: row.description || "",
      type: row.type || "",
    });
  }

  function cancel() {
    setEditId(null);
    setForm({});
  }

  async function loadCategories() {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(CATEGORY_URL, {
        headers: { Authorization: token ? `Token ${token}` : "" },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Failed to load categories");
      }
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
        ? data.data
        : [];
      setRows(list);
    } catch (err) {
      setError(err.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!form.category || !form.short_form) return alert("Category and short form are required");
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const payload = {
        category: form.category,
        short_form: form.short_form,
        description: form.description,
        type: form.type,
      };

      if (editId === "new") {
        const res = await fetch(CATEGORY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Token ${token}` : "",
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || "Failed to create category");
        }
        const newRow = data?.data || data;
        setRows((prev) => [...prev, newRow]);
      } else {
        const res = await fetch(`${CATEGORY_URL}${editId}/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Token ${token}` : "",
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || "Failed to update category");
        }
        const updated = data?.data || data;
        setRows((prev) => prev.map((r) => (r.id === editId ? updated : r)));
      }
      cancel();
    } catch (err) {
      setError(err.message || "Failed to save category");
    } finally {
      setLoading(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this category?")) return;
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${CATEGORY_URL}${id}/`, {
        method: "DELETE",
        headers: { Authorization: token ? `Token ${token}` : "" },
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json();
        throw new Error(data?.message || "Failed to delete category");
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete category");
    } finally {
      setLoading(false);
    }
  }

  const typeCounts = types.reduce((acc, t) => {
    acc[t] = rows.filter((r) => r.type === t).length;
    return acc;
  }, {});

  return (
    <div>
      <Navbar />
      <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Category Master</h2>
          <p style={s.subtitle}>Manage product & business categories</p>
        </div>
        <button style={s.btnPrimary} onClick={startAdd}>+ Add Category</button>
      </div>

      {/* Stat cards */}
      <div style={s.statsRow}>
        <StatCard label="Total" value={rows.length} />
        {Object.entries(typeCounts).map(([t, c]) => (
          <StatCard key={t} label={t} value={c} />
        ))}
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          style={s.input}
          placeholder="Search category or short form..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={s.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      {error && <div style={s.error}>{error}</div>}

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {["Category", "Short Form", "Meaning", "Type", "Actions"].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* New row at top */}
            {editId === "new" && (
              <EditRow
                form={form}
                onChange={(k, v) => setForm((p) => ({ ...p, [k]: v }))}
                onSave={save}
                onCancel={cancel}
              />
            )}

            {loading && (
              <tr>
                <td colSpan={5} style={s.empty}>Loading categories...</td>
              </tr>
            )}

            {filtered.length === 0 && !loading && editId !== "new" && (
              <tr>
                <td colSpan={5} style={s.empty}>No categories found</td>
              </tr>
            )}

            {filtered.map((row) =>
              editId === row.id ? (
                <EditRow
                  key={row.id}
                  form={form}
                  onChange={(k, v) => setForm((p) => ({ ...p, [k]: v }))}
                  onSave={save}
                  onCancel={cancel}
                />
              ) : (
                <tr key={row.id} style={s.tr}>
                  <td style={s.td}><span style={s.bold}>{row.category}</span></td>
                  <td style={s.td}><span style={s.badge}>{row.short_form}</span></td>
                  <td style={{ ...s.td, color: "#888", fontSize: 12 }}>{row.description || "—"}</td>
                  <td style={s.td}>
                    <span style={{ ...s.typeBadge, ...(TYPE_COLORS[row.type] || {}) }}>
                      {row.type}
                    </span>
                  </td>
                  <td style={{ ...s.td, display: "flex", gap: 6 }}>
                    <button style={s.iconBtn} onClick={() => startEdit(row)}>✎</button>
                    <button style={{ ...s.iconBtn, color: "#c0392b" }} onClick={() => remove(row.id)}>✕</button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function EditRow({ form, onChange, onSave, onCancel }) {
  return (
    <tr style={{ background: "#f9f9f9" }}>
      <td style={s.td}><input style={s.cellInput} placeholder="Category name" value={form.category || ""} onChange={(e) => onChange("category", e.target.value)} /></td>
      <td style={s.td}><input style={s.cellInput} placeholder="SHORT" value={form.short_form || ""} onChange={(e) => onChange("short_form", e.target.value)} /></td>
      <td style={s.td}><input style={s.cellInput} placeholder="Meaning / description" value={form.description || ""} onChange={(e) => onChange("description", e.target.value)} /></td>
      <td style={s.td}><input style={s.cellInput} placeholder="product / channel / business" value={form.type || ""} onChange={(e) => onChange("type", e.target.value)} /></td>
      <td style={{ ...s.td, display: "flex", flexDirection: "column", gap: 4 }}>
        <button style={s.saveBtn} onClick={onSave}>Save</button>
        <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
      </td>
    </tr>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={s.statCard}>
      <div style={s.statVal}>{value}</div>
      <div style={s.statLabel}>{label.toUpperCase()}</div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page:       { padding: "24px", fontFamily: "sans-serif", maxWidth: 1100 },
  header:     { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 },
  title:      { fontSize: 20, fontWeight: 600, margin: 0 },
  subtitle:   { fontSize: 12, color: "#888", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" },
  btnPrimary: { background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, cursor: "pointer", fontWeight: 500 },
  statsRow:   { display: "flex", gap: 10, marginBottom: 16 },
  statCard:   { background: "#f5f5f5", borderRadius: 8, padding: "10px 16px", minWidth: 100 },
  statVal:    { fontSize: 22, fontWeight: 600, color: "#1a1a1a" },
  statLabel:  { fontSize: 10, color: "#888", marginTop: 2, letterSpacing: "0.06em" },
  filterRow:  { display: "flex", gap: 10, marginBottom: 14 },
  input:      { flex: 1, border: "1px solid #ddd", borderRadius: 8, padding: "7px 12px", fontSize: 13, outline: "none" },
  select:     { border: "1px solid #ddd", borderRadius: 8, padding: "7px 12px", fontSize: 13, outline: "none", background: "#fff" },
  tableWrap:  { border: "1px solid #eee", borderRadius: 10, overflow: "hidden" },
  table:      { width: "100%", borderCollapse: "collapse" },
  th:         { background: "#f7f7f7", padding: "9px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #eee" },
  tr:         { borderBottom: "1px solid #f0f0f0" },
  td:         { padding: "10px 14px", fontSize: 13, verticalAlign: "middle" },
  bold:       { fontWeight: 500 },
  badge:      { background: "#f0f0f0", border: "1px solid #e0e0e0", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontFamily: "monospace", fontWeight: 600 },
  typeBadge:  { fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "#f0f0f0", color: "#555" },
  iconBtn:    { background: "none", border: "none", cursor: "pointer", padding: "4px 6px", fontSize: 15, color: "#aaa", borderRadius: 4 },
  cellInput:  { width: "100%", border: "1px solid #ddd", borderRadius: 6, padding: "5px 8px", fontSize: 12, outline: "none" },
  saveBtn:    { border: "1px solid #2ecc71", color: "#27ae60", background: "none", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: "pointer" },
  cancelBtn:  { border: "none", background: "none", color: "#aaa", fontSize: 11, cursor: "pointer", padding: "3px 6px" },
  empty:      { textAlign: "center", padding: "2rem", color: "#bbb", fontSize: 13 },
  error:      { color: "#c0392b", marginBottom: 10, fontSize: 12 },
};
